'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Check, X, AlertTriangle } from 'lucide-react';
import type {
  Match,
  MatchResult,
  SetScore,
  TeamGames,
} from '@/types/tournament.model';
import { SpecialResultType } from '@/types/tournament.model';
import { SetScoreEditor } from './SetScoreEditor';
import { ConfirmResultModal } from './ConfirmResultModal';

interface MatchEditModalProps {
  match: Match;
  team1Name: string;
  team2Name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (result: MatchResult) => Promise<void>;
  isSubmitting: boolean;
}

// Helper function to determine current special result type from match result
function getInitialSpecialResultType(result?: MatchResult): SpecialResultType {
  // Backend semantics: teamXWalkover === teamX did walkover (absence/retirement),
  // meaning the OTHER team is the winner.
  if (result?.team1Walkover && result?.team2Walkover)
    return SpecialResultType.DOUBLE_WALKOVER;
  if (result?.team1Walkover) return SpecialResultType.TEAM2_WALKOVER;
  if (result?.team2Walkover) return SpecialResultType.TEAM1_WALKOVER;
  return SpecialResultType.NONE;
}

export function MatchEditModal({
  match,
  team1Name,
  team2Name,
  open,
  onOpenChange,
  onSave,
  isSubmitting,
}: MatchEditModalProps) {
  const initialSetScores =
    (match.result?.setScores?.length ?? 0) > 0
      ? [...(match.result?.setScores ?? [])]
      : [{ team1Games: 0, team2Games: 0 }];

  const [setScores, setSetScores] = useState<SetScore[]>(initialSetScores);
  const [team1Score, setTeam1Score] = useState(match.result?.team1Score || 0);
  const [team2Score, setTeam2Score] = useState(match.result?.team2Score || 0);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Special result state
  const [showSpecialResultSelector, setShowSpecialResultSelector] =
    useState<boolean>(
      () =>
        getInitialSpecialResultType(match.result) !== SpecialResultType.NONE,
    );
  const [specialResultType, setSpecialResultType] = useState<SpecialResultType>(
    () => getInitialSpecialResultType(match.result),
  );

  useEffect(() => {
    let calculatedTeam1Score = 0;
    let calculatedTeam2Score = 0;

    setScores.forEach((set) => {
      if (set.team1Games > set.team2Games) calculatedTeam1Score++;
      else if (set.team2Games > set.team1Games) calculatedTeam2Score++;
    });

    setTeam1Score(calculatedTeam1Score);
    setTeam2Score(calculatedTeam2Score);
  }, [setScores]);

  const handleSetScoreChange = (
    index: number,
    team: TeamGames.TEAM_1 | TeamGames.TEAM_2,
    value: number,
  ) => {
    const newSetScores = [...setScores];
    newSetScores[index] = {
      ...newSetScores[index],
      [team]: value,
    };
    setSetScores(newSetScores);
  };

  const addSet = () => {
    setSetScores([...setScores, { team1Games: 0, team2Games: 0 }]);
  };

  const removeSet = (index: number) => {
    if (setScores.length > 1) {
      const newSetScores = [...setScores];
      newSetScores.splice(index, 1);
      setSetScores(newSetScores);
    }
  };

  const handleContinue = () => {
    setShowConfirmDialog(true);
  };

  const handleAddSpecialResult = () => {
    setShowSpecialResultSelector(true);
  };

  const handleRemoveSpecialResult = () => {
    setSpecialResultType(SpecialResultType.NONE);
    setShowSpecialResultSelector(false);
  };

  const handleSpecialResultChange = (value: string) => {
    setSpecialResultType(value as SpecialResultType);
  };

  const confirmAndSubmitResult = async () => {
    const result: MatchResult = {
      team1Score,
      team2Score,
      sportType: match.sport?.uid,
      setScores,
      isValid: true,
      // Only one special flag can be true at a time, rest are false
      // UI semantics: TEAM1_WALKOVER === team1 wins by opponent walkover
      // Payload semantics: team2Walkover === team2 did walkover
      team1Walkover:
        specialResultType === SpecialResultType.TEAM2_WALKOVER ||
        specialResultType === SpecialResultType.DOUBLE_WALKOVER,
      team2Walkover:
        specialResultType === SpecialResultType.TEAM1_WALKOVER ||
        specialResultType === SpecialResultType.DOUBLE_WALKOVER,
    };

    await onSave(result);
    setShowConfirmDialog(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <>
      <Dialog
        open={open && !showConfirmDialog}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            onOpenChange(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Editar resultado</DialogTitle>
            <DialogDescription>
              Ingresa los resultados de cada set para el partido entre{' '}
              {team1Name} y {team2Name}.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto py-4 pr-1">
            <SetScoreEditor
              team1Name={team1Name}
              team2Name={team2Name}
              setScores={setScores}
              onSetScoreChange={handleSetScoreChange}
              onAddSet={addSet}
              onRemoveSet={removeSet}
            />

            {/* Special Result Section */}
            <div className="mt-4 border-t pt-4">
              {!showSpecialResultSelector ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-slate-300 text-slate-600 hover:bg-slate-50"
                  onClick={handleAddSpecialResult}
                >
                  <AlertTriangle className="mr-1 h-4 w-4" />
                  Añadir resultado especial
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Resultado especial
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-1 text-xs text-red-600 hover:text-red-700"
                      onClick={handleRemoveSpecialResult}
                    >
                      <X className="mr-1 h-3 w-3" />
                      Quitar
                    </Button>
                  </div>
                  <Select
                    value={
                      specialResultType === SpecialResultType.NONE
                        ? undefined
                        : specialResultType
                    }
                    onValueChange={handleSpecialResultChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecciona el resultado especial" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(var(--radix-select-trigger-width))]">
                      <SelectItem value={SpecialResultType.TEAM1_WALKOVER}>
                        Gana {team1Name} por ausencia/abandono
                      </SelectItem>
                      <SelectItem value={SpecialResultType.TEAM2_WALKOVER}>
                        Gana {team2Name} por ausencia/abandono
                      </SelectItem>
                      <SelectItem
                        value={SpecialResultType.DOUBLE_WALKOVER}
                        className="whitespace-normal leading-snug"
                      >
                        Doble walkover: Ambos equipos presentaron
                        ausencia/abandono
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Cancelar
            </Button>
            <Button onClick={handleContinue} disabled={isSubmitting}>
              <Check className="mr-1 h-3.5 w-3.5" />
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirmation modal (second step) */}{' '}
      <ConfirmResultModal
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        team1Name={team1Name}
        team2Name={team2Name}
        team1Score={team1Score}
        team2Score={team2Score}
        setScores={setScores}
        specialResultType={specialResultType}
        onConfirm={confirmAndSubmitResult}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
